import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Modal, FormLayout, TextField, Text, Banner } from "@shopify/polaris";
// These are Polaris Combobox/Popover-based (file type, series) or use a
// Polaris Popover of their own (the date pickers) — they need a genuine
// Polaris `Modal` (a `data-polaris-layer`) as their host so their own
// dropdowns get a z-index above the modal. A catalyst-ui-kit `Dialog` (plain
// Tailwind/Headless UI, no such layer) would leave them rendering behind it —
// unlike MakeMainDocumentModal, which only needs the plain, non-portalled
// <Select> that FileCategorySelect renders in single-select mode.
import FileCategorySelect from "components/file-category-select/file-category-select";
import SearchableFilesTypesSelect from "components/searchable-files-types-select/searchable-files-types-select";
import SearchableSeriesSelect from "components/searchable-series-select/searchable-series-select";
import DatePickerx from "features/datepicker/date-picker";
import Loading from "components/loading/Loading";
import { fetchById, patchFile, setFileParent } from "services/upload-file.service";

interface MakeSeparateDocumentModalProps {
  companyId: string;
  fileId: string;
  // A signed or annulled document may still be moved (that is exactly the
  // case this feature is for — a signed contract filed in the wrong place),
  // but files/patch has no signed-document guard of its own, so this modal
  // must never use it to edit fields on one. See isLocked below.
  isSigned?: boolean;
  annulledAt?: string | null;
  open: boolean;
  onClose: () => void;
  onDone: () => void;
}

/**
 * Turns a child document into a separate, top-level one (Desk #237 follow-up
 * to AttachToDocumentModal/MakeMainDocumentModal): unlike a plain detach, a
 * document filed as a child is often missing the fields an independent
 * document needs (file type, category, ...), so this collects them first.
 *
 * Submits in two steps:
 *   a) setFileParent(..., { parentId: null, categories }) detaches it. If the
 *      backend refuses (a module-owned child, an invalid category, someone
 *      else already detached it in the meantime), nothing else has run yet —
 *      the document is untouched.
 *   b) patchFile() saves the fields WITHOUT `relatedTo` — files/patch.js only
 *      regenerates the document name from the file type when `relatedTo` is
 *      absent, which is what replaces the child's inherited
 *      "<context> - <parent name>" name.
 * The detach runs first on purpose: patching first (the previous order) left
 * a document renamed under its old parent whenever the detach was then
 * refused. If (b) fails after (a) succeeded, that is reported separately —
 * the document did leave its parent, its fields just did not save.
 *
 * A signed/annulled document (isLocked) skips (b) entirely: only the
 * category is required, and the other fields render disabled — they show
 * the document's current values but are never sent to files/patch, which has
 * no signed-document guard of its own (unlike the row's Edit action).
 *
 * files/patch.js always writes date/dateTo/series/externalNumber/assignedUser
 * /visibleInAllProfile/source from the request body (an absent field is sent
 * as null/""), so every field fetched below is round-tripped back on submit
 * even when the user never touches it — leaving one out would blank it.
 * customer/users/projects/assets/customFields are round-tripped too: patch.js
 * only touches those relations when a field is present, so they are never at
 * risk of being cleared, but its name-regeneration step (FileTypeService.
 * getFileName) reads them straight from the request body — omitting them
 * would rebuild the name with those placeholders empty even though the
 * links themselves remain.
 */
export default function MakeSeparateDocumentModal({
  companyId,
  fileId,
  isSigned = false,
  annulledAt = null,
  open,
  onClose,
  onDone,
}: MakeSeparateDocumentModalProps) {
  const { t } = useTranslation();
  const isLocked = Boolean(isSigned || annulledAt);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // Set once setFileParent succeeded but the field-saving step then failed —
  // the document did become separate, its fields just did not save.
  const [patchErrorMessage, setPatchErrorMessage] = useState<string | null>(
    null
  );

  const [categories, setCategories] = useState<string[]>([]);
  const [fileType, setFileType] = useState("");
  const [fileTypeContext, setFileTypeContext] = useState("");
  const [series, setSeries] = useState("");
  const [externalNumber, setExternalNumber] = useState("");
  // Kept as ISO strings (DatePickerx's own PropTypes declare `selected` as a
  // string) — its onChange hands back a Date, converted back below.
  const [date, setDate] = useState<string | null>(null);
  const [dateTo, setDateTo] = useState<string | null>(null);
  // Preserved as fetched and sent back unchanged — see the files/patch.js
  // note above.
  const [assignedUser, setAssignedUser] = useState("");
  const [visibleInAllProfile, setVisibleInAllProfile] = useState(false);
  const [source, setSource] = useState("");
  // Relations — not editable here, only round-tripped so the regenerated
  // name still reflects them (see the file-level note above).
  const [customer, setCustomer] = useState<string[]>([]);
  const [users, setUsers] = useState<string[]>([]);
  const [projects, setProjects] = useState<string[]>([]);
  const [assets, setAssets] = useState<string[]>([]);
  const [customFields, setCustomFields] = useState<any[]>([]);

  // Set when a move succeeded but saving the fields did not: the list behind
  // the modal needs a refresh, deferred until the modal is closed.
  const refreshOnCloseRef = useRef(false);

  const resetFields = useCallback(() => {
    setCategories([]);
    setFileType("");
    setFileTypeContext("");
    setSeries("");
    setExternalNumber("");
    setDate(null);
    setDateTo(null);
    setAssignedUser("");
    setVisibleInAllProfile(false);
    setSource("");
    setCustomer([]);
    setUsers([]);
    setProjects([]);
    setAssets([]);
    setCustomFields([]);
  }, []);

  useEffect(() => {
    if (!open || !fileId || !companyId) return;
    let cancelled = false;
    async function load() {
      setIsLoading(true);
      setErrorMessage(null);
      setPatchErrorMessage(null);
      // Never show another document's values while this one loads (or if it
      // fails to): the modal is reused for every child row, and a pending
      // refresh from a previous attempt must not leak into this one.
      resetFields();
      refreshOnCloseRef.current = false;
      try {
        const data = await fetchById(fileId, companyId);
        if (cancelled) return;
        setCategories((data.categories || []).map((c: any) => c.id));
        setFileType(data.fileType || "");
        setFileTypeContext(data.fileTypeContext || "");
        setSeries(data.series?.id || "");
        setExternalNumber(data.externalNumber || "");
        setDate(data.date || null);
        setDateTo(data.dateTo || null);
        setAssignedUser(data.assignedUser?.id || "");
        setVisibleInAllProfile(!!data.visibleInAllProfile);
        setSource(data.source || "");
        setCustomer((data.customer || []).map((c: any) => c.id));
        setUsers((data.users || []).map((u: any) => u.id));
        setProjects((data.projects || []).map((p: any) => p.id));
        setAssets((data.assets || []).map((a: any) => a.id));
        setCustomFields(data.customFields || []);
      } catch (e) {
        if (!cancelled) {
          resetFields();
          setErrorMessage("error");
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, fileId, companyId]);

  const handleClose = () => {
    if (isSubmitting) return;
    if (refreshOnCloseRef.current) {
      refreshOnCloseRef.current = false;
      onDone();
    }
    onClose();
  };

  // FileCategorySelect's placeholder option has value "" (its own single-
  // select Select always includes a " - " entry) — filter it out everywhere
  // a non-empty category is what actually matters.
  const validCategories = categories.filter(Boolean);

  const canSubmit = isLocked
    ? !isLoading && !isSubmitting && validCategories.length > 0
    : !isLoading &&
      !isSubmitting &&
      validCategories.length > 0 &&
      !!fileType &&
      !!date;

  const handleDateChange = (value: Date | null) =>
    setDate(value ? value.toISOString() : null);
  const handleDateToChange = (value: Date | null) =>
    setDateTo(value ? value.toISOString() : null);

  const handleConfirm = async () => {
    if (!canSubmit) return;
    setIsSubmitting(true);
    setErrorMessage(null);
    setPatchErrorMessage(null);
    try {
      await setFileParent(companyId, fileId, {
        parentId: null,
        categories: validCategories,
      });
    } catch (e: any) {
      setIsSubmitting(false);
      setErrorMessage(e?.response?.data?.message || "error");
      return;
    }
    if (isLocked) {
      // Signed/annulled document — only the move happened, its fields were
      // never sent to files/patch.
      setIsSubmitting(false);
      refreshOnCloseRef.current = false;
      onDone();
      onClose();
      return;
    }
    try {
      const customFieldsPayload = customFields.reduce(
        (acc: Record<string, any>, field: any) => {
          acc[field.id] = field.value;
          return acc;
        },
        {}
      );
      await patchFile(
        {
          id: fileId,
          fileType,
          fileTypeContext,
          series,
          externalNumber,
          date,
          dateTo,
          assignedUser,
          visibleInAllProfile,
          source,
          customer,
          users,
          projects,
          assets,
          customFields: customFieldsPayload,
        },
        companyId
      );
      setIsSubmitting(false);
      refreshOnCloseRef.current = false;
      onDone();
      onClose();
    } catch (e: any) {
      setIsSubmitting(false);
      setPatchErrorMessage(e?.response?.data?.message || "error");
      // The document already left its parent, so the list behind this modal
      // is stale — but refreshing it now can unmount this modal (the parent
      // renders an empty state once its last child is gone) and take the
      // warning with it. Refresh when the user closes it instead.
      refreshOnCloseRef.current = true;
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={t("make_separate_document")}
      primaryAction={{
        content: t("make_separate_document_confirm"),
        onAction: handleConfirm,
        disabled: !canSubmit,
        loading: isSubmitting,
      }}
      secondaryActions={[
        {
          content: t("cancel"),
          onAction: handleClose,
          disabled: isSubmitting,
        },
      ]}
    >
      <Modal.Section>
        {isLoading ? (
          <Loading />
        ) : (
          <FormLayout>
            {isLocked ? (
              <Banner tone="warning">
                {t("make_separate_document_signed_note")}
              </Banner>
            ) : (
              <Text as="p" variant="bodyMd" tone="subdued">
                {t("make_separate_document_hint")}
              </Text>
            )}
            <FileCategorySelect
              value={categories}
              onChange={setCategories}
              isRequired
              subject="document"
            />
            <SearchableFilesTypesSelect
              value={fileType}
              onChange={setFileType}
              isRequired={!isLocked}
              disabled={isLocked}
            />
            <SearchableSeriesSelect
              value={series}
              onChange={setSeries}
              disabled={isLocked}
            />
            {!series && (
              <Text as="p" variant="bodySm" tone="subdued">
                {t("not_set")}
              </Text>
            )}
            <TextField
              label={t("externalNumber")}
              value={externalNumber}
              onChange={setExternalNumber}
              autoComplete="off"
              disabled={isLocked}
              helpText={!externalNumber ? t("not_set") : undefined}
            />
            <DatePickerx
              title={t("document_date")}
              onChange={handleDateChange}
              selected={date}
              disableDatesAfter={null}
              readOnly={isLocked}
            />
            {!date && (
              <Text as="p" variant="bodySm" tone="subdued">
                {t("not_set")}
              </Text>
            )}
            <DatePickerx
              title={t("dateTo")}
              onChange={handleDateToChange}
              selected={dateTo}
              disableDatesAfter={null}
              readOnly={isLocked}
            />
            {!dateTo && (
              <Text as="p" variant="bodySm" tone="subdued">
                {t("not_set")}
              </Text>
            )}
            {errorMessage && <Banner tone="critical">{t(errorMessage)}</Banner>}
            {patchErrorMessage && (
              <Banner tone="warning">
                {t("make_separate_document_patch_failed")}{" "}
                {t(patchErrorMessage)}
              </Banner>
            )}
          </FormLayout>
        )}
      </Modal.Section>
    </Modal>
  );
}
